import { createFeatureSelector, createSelector } from '@ngrx/store';
import { TenantModel } from '../core/models/tenant';
import { ConfigurationGroup } from '../shared/enums/configurationGroup.enum';
import { SystemConfigurationName } from '../shared/enums/systemConfigurationName.enum';
import { LobbyLocationSelectorInfo } from '../shared/models/lobby-location-selected.interface';
import { IProductLicense } from '../shared/models/store models/product-license.interface';
import { LicenseType } from './../shared/enums/LicenseType.enum';
import { MessageHubState } from './app.reducer';
import { CoreState } from './core.state';

const getMessageHubFeatureState = createFeatureSelector<MessageHubState>('messageHub');
const getCoreFeatureState = createFeatureSelector<CoreState>('CoreState');

export const getIsReConnected = createSelector(
    getMessageHubFeatureState,
    state => state.isReconnected
);

export const getIsConnectionStarted = createSelector(
    getMessageHubFeatureState,
    state => state.isConnectionStarted
);

export const getAvailableActiveBranches = createSelector(
    getCoreFeatureState,
    (state: CoreState) => state.availableActiveBranches
);

export const getSignedInStatus = createSelector(
    getCoreFeatureState,
    (state: CoreState) => state.UserSignedInStatus
);

export const getShouldPromptUserToSignIn = createSelector(
    getCoreFeatureState,
    state => state.PromptSigningPopup
);

export const getUnableToCheckInPopupPrompt = createSelector(
    getCoreFeatureState,
    (state: CoreState) => state.UnableToCheckInPopup
)

export const getShouldSignInLocationDisabled = createSelector(
    getCoreFeatureState,
    state => state.lobbySelectorChange
);

export const getUnableToSignInPopupPrompt = createSelector(
    getCoreFeatureState,
    (state: CoreState) => state.UnableToSigningPopup
)

export const getUserFullName = createSelector(
    getCoreFeatureState,
    state => `${(state.userInfo?.firstName ?? '')} ${(state.userInfo?.lastName ?? '')}`
);

export const getLoggedInUserNotificationGroup = createSelector(
    getCoreFeatureState,
    state => state.userInfo?.notificationGroupIds
);

export const getLoggedInUserId = createSelector(
    getCoreFeatureState,
    state => state.userInfo?.userId
);

export const getLoggedInUserInfo = createSelector(
    getCoreFeatureState,
    state => state.userInfo
);

export const getLoggedInUserHomeLocationInfo = createSelector(
    getCoreFeatureState,
    state => ({
        homeLocationId: state.userInfo?.homeLocationId,
        homeLocationName: state.userInfo?.homeLocationName,
        homeLocationCode: state.userInfo?.homeLocationCode,
        utcOffSetForHomeLocationTimeZone: state.userInfo?.utcOffSetForHomeLocationTimeZone,
        prefferedTimeZone: state.userInfo?.prefferedTimeZone
    })
);

export const getUrlCodeForCurrentTenant = createSelector(
    getCoreFeatureState,
    state => state.urlCode
);

export const getIsSystemConfigDataLoaded = createSelector(
    getCoreFeatureState,
    state => state.systemConfigLoaded
);

export const getCurrentLocationTimeZone = createSelector(
    getCoreFeatureState,
    state => state.userInfo?.utcOffSetForHomeLocationTimeZone
);

// This selector is added for lobby signing In case of update please refer the requirement CC-289
export const getLocationSelected = createSelector(getCoreFeatureState,
    (state) => ({
        locationName:
            state.LocationSelectorInfo != null
                ? state.LocationSelectorInfo.locationName
                : state.userInfo?.homeLocationName,
        homeLocationId:
            state.LocationSelectorInfo != null
                ? state.LocationSelectorInfo.id
                : state.userInfo?.homeLocationId,
        homeLocationCode:
            state.LocationSelectorInfo != null
                ? state.LocationSelectorInfo.locationCode
                : state.userInfo?.homeLocationCode,
        utcOffSet:
            state.LocationSelectorInfo != null
                ? state.LocationSelectorInfo?.utcoffsetInMinutes
                : state.userInfo?.utcOffSetForHomeLocationTimeZone,
        timeZoneCode:
            state.LocationSelectorInfo != null
                ? state.LocationSelectorInfo?.utcoffsetInMinutes
                : state.userInfo?.prefferedTimeZone.tzCode,
    }));

export const getLobbyLocationSelected = createSelector(
    getCoreFeatureState,
    (state): LobbyLocationSelectorInfo => ({
        locationName:
            state.LobbyLocationSelectorInfo != null
                ? state.LobbyLocationSelectorInfo.locationName
                : "",
        locationId:
            state.LobbyLocationSelectorInfo != null &&
            typeof state.LobbyLocationSelectorInfo.id == 'number'
                ? state.LobbyLocationSelectorInfo.id
                : 0,
        locationCode:
            state.LobbyLocationSelectorInfo != null
                ? state.LobbyLocationSelectorInfo.locationCode
                : "",
        utcOffSet:
            state.LobbyLocationSelectorInfo != null
                ? state.LobbyLocationSelectorInfo.utcoffsetInMinutes
                : 0,
        timezoneId:
            state.LobbyLocationSelectorInfo != null
                ? state.LobbyLocationSelectorInfo.timezoneId
                : "Sri Lanka Standard Time",
        timeZoneName:
            state.LobbyLocationSelectorInfo != null
                ? state.LobbyLocationSelectorInfo.timeZoneName
                : "Asia/Colombo",
        dropDownDisabled:
            state.LobbyLocationSelectorInfo != null &&
            typeof state.LobbyLocationSelectorInfo.dropDownDisabled == 'boolean'
                ? state.LobbyLocationSelectorInfo.dropDownDisabled
                : false,
        lastLocationAutoWraupTime:
            state.LobbyLocationSelectorInfo != null
                ? state.LobbyLocationSelectorInfo.lastLocationAutoWraupTime
                : null,
    })
);

export const getACLocationSelected = createSelector(
    getCoreFeatureState,
    state => state.LocationSelectorInfo?.id
);

export const getACLocationCodeSelected = createSelector(
    getCoreFeatureState,
    state => state.ACLocationCode
);

export const getLobbyLocationSelctorStatus = createSelector(
    getCoreFeatureState,
    state => state.locationSelectorStatus
);

export const getLocationCodeSelected = createSelector(
    getCoreFeatureState, state => state.userInfo?.homeLocationCode
);

export const getIdentityProviderType = createSelector(
    getCoreFeatureState, state => state.userInfo?.identityProvderType
);

export const getUserType = createSelector(
    getCoreFeatureState, state => state.userInfo?.userType
);

// Since this update was done to reduce an extra call to back end forth from id server to admin service
// Admin service would not have an idea about whether this tenant is default or not hence returning false would be ideal
// Assumption with investigation is currently we use isDefault on when tenant switching hence if the available 
// tenant list contains the default value that would be sufficient
export const getCurrentTenant = createSelector(
    getCoreFeatureState,
    state => ({
        tenantID: state.userInfo?.tenantId,
        tenantDisplayName: state.userInfo?.tenantName,
        isDefault: false
    } as TenantModel)
);

export const getAvailableTenants = createSelector(
    getCoreFeatureState,
    state => state.availableTenants
);

export const getSystemConfigurations = createSelector(
    getCoreFeatureState,
    state => state.systemConfigurations
);

export const getACLicenseStatus = createSelector(
    getCoreFeatureState,
    state => {
        const licensingSystemConfigValues = state.systemConfigurations
            .filter(c => c.configurationGroup === ConfigurationGroup.LicenseSettings);
        if (licensingSystemConfigValues && licensingSystemConfigValues.length > 0) {
            const productLicenseValues = JSON.parse(licensingSystemConfigValues[0].value);
            if (productLicenseValues.find((x: IProductLicense) => x.ProductId == LicenseType.AC_License).IsEnabled) {
                return true;
            }
            return false;
        }
        return false;
    }
);

export const getLTLicenseStatus = createSelector(
    getCoreFeatureState,
    state => {
        const licensingSystemConfigValues = state.systemConfigurations
            .filter(c => c.configurationGroup === ConfigurationGroup.LicenseSettings);
        if (licensingSystemConfigValues && licensingSystemConfigValues.length > 0) {
            const productLicenseValues = JSON.parse(licensingSystemConfigValues[0].value);
            if (productLicenseValues.find((x: IProductLicense) => x.ProductId == LicenseType.LT_License).IsEnabled) {
                return true;
            }
            return false;
        }
        return false;
    }
);

export const getLobbyShowCategory = createSelector(
    getCoreFeatureState,
    state => {
        const lobbyConfigurations = state.systemConfigurations
            .filter(c => c.configurationGroup === ConfigurationGroup.LobbySettings);
        if (lobbyConfigurations.length > 0) {
            const lobbyConfigValue = lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.ShowCategories);
            return lobbyConfigValue?.value === '1';
        }
        return false;
    }
);

export const GetUserPermissionList = createSelector(
    getCoreFeatureState,
    state => state.userInfo?.userPermissions
);

export const getACClientConfiguration = createSelector(
    getCoreFeatureState,
    state => state.acClientConfiguration
);

export const getTenantLogoUrl = createSelector(
    getCoreFeatureState,
    state => state.tenantLogo
);

export const getSyncStatus = createSelector(
    getCoreFeatureState,
    state => state.syncStatus
);

export const getClientCreationStatus = createSelector(
    getCoreFeatureState,
    state => state.clientCreationStatus
);

export const getReserveWithGoogleStatus = createSelector(
    getCoreFeatureState,
    state => {

        const reserveWithGoogleConfigurations = state.systemConfigurations.filter(c => c.configurationGroup === 'ReserveWithGoogleSettings');
        if (reserveWithGoogleConfigurations.length > 0) {

            const reserveWithGoogleConfigValue= reserveWithGoogleConfigurations.find(ac => ac.configurationName === 'EnableReserveWithGoogle');

            return (reserveWithGoogleConfigValue?.value === '1');

        }
        return false;
    }
);